/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from 'react';
import { clientState, connectToPhotonRoom, getMyRoomPlayers, setPhotonEventHandlers } from './utils/photon/client';
import { photonApi } from './utils/photon/instance';
import { POLLING_INTERVAL_WAITING_PLAYER_SECONDS } from './config/config';
import { VoteWitness } from './VoteWitness';
import { IS_LOCAL } from './config/env';
import './Lobby.scss';


export const Lobby = ({ appData, addMsg, onStartGame, clearInvite }) => {
  const {
    loggedInUser: currentUser,
    isInviteMatch = false,
    players: expectedPlayers = {},
    room = {},
    waitingOpponent, // could remove
  } = appData;
  const previousMatchRoom = room.roomID;

  const { player1, player2 } = expectedPlayers;
  const invitedPlayer = isInviteMatch ? (currentUser === player1 ? player2 : player1) : null;

  const intervalRef = useRef(null);
  
  // AUTO-CONNECT LOBBY
  useEffect(() => {
    if (photonApi.client || intervalRef.current) return; // rematch
    IS_LOCAL && addMsg('Lobby in 1st match.', { id: 'lobby', dev: true });
    const connectLobby = async () => {
      // 2. Set Photon event handlers for managing player join events
      setPhotonEventHandlers({
        onJoinRoom: ({ roomName, masterName, masterColor } = {}) => {
          addMsg(`Joined the game created by ${masterName} (${masterColor})`, { id: 'lobby:init', devData: `Room: ${roomName}` });
          // keep checking until someone joins
          checkPlayersInRoom({ roomName, masterName, masterColor});
          intervalRef.current = setInterval(() => {
            checkPlayersInRoom({ roomName, masterName, masterColor, mute: true });
          }, POLLING_INTERVAL_WAITING_PLAYER_SECONDS * 1000);
        },
        onPlayerJoin: (playerName) => {
          addMsg(`Another player joined?? ${playerName}`, { id: 'lobby:init' });
        },
        onJoinRoomFailed: () => {
          addMsg('Failed to join room.', { id: 'lobby:init' });
        },
      });
      // 1. Connect to the Photon room based on match parameters
      await connectToPhotonRoom({ appData, addMsg });
    };
    connectLobby();
    // on unmount:
    return () => clearInterval(intervalRef.current);
  }, []);

  // CHECK ALL PLAYERS READY
  const checkPlayersInRoom = ({ roomName, masterName, masterColor, mute = false } = {}) => {
    if (!waitingOpponent || !intervalRef.current) return; // could remove
    !mute && addMsg('checking players in room to see if we can start the game', { id: 'lobby:check&go' });
    const playerList = getMyRoomPlayers() || [];
    if (isInviteMatch) {
      // INVITE
      const opponent = currentUser === expectedPlayers.player1
        ? expectedPlayers.player2
        : expectedPlayers.player1;
      const inviteMatchCanStart = playerList.includes(currentUser) && playerList.includes(opponent);
      if (inviteMatchCanStart) {
        addMsg(`The opponent ${opponent} has joined. Starting game...`, { id: 'lobby:check&go' });
        startGame({ playerList, roomName, masterName, masterColor });
      } else {
        !mute && addMsg(`Still waiting for ${opponent} to join the game.`, { id: 'lobby:check&go' });
      }
    } else {
      // PUBLIC MATCH
      const regularMatchCanStart = playerList.length === 2 && playerList[0] !== playerList[1];
      if (regularMatchCanStart) {
        addMsg('An opponent has joined. Starting game...', { id: 'lobby:check&go' });
        startGame({ playerList, roomName, masterName, masterColor });
      } else {
        !mute && addMsg(`Waiting for any player to join my game. Players in my room: ${playerList.length}`, { id: 'lobby:check&go' });
      }
    }
  };

  const startGame = ({ playerList, roomName, masterName, masterColor }) => {
    // cleanup
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    // update status
    clientState.playing = true;
    // prep
    const masterOpponent = playerList.find(name => name !== masterName);
    // call parent to load Game component
    onStartGame({
      activePlayers: { player1: masterName, player2: masterOpponent },
      masterName, masterColor, roomName,
    });
  };

  return (
    <div className="lobby sci-fi-container">
      {previousMatchRoom && (
        <p className="sci-fi-text sci-fi-fade-in">Ready for another match?</p>
      )}
      <p className="sci-fi-text waiting-text">
        Waiting for {isInviteMatch ? invitedPlayer : 'an opponent'}...
      </p>
      {isInviteMatch && (
        <div className="sci-fi-button-container">
          OR &nbsp;
          <button
            id="clear-lobby-filters"
            className="sci-fi-button"
            onClick={() => clearInvite({ clearFilters: true })}
          >
            Accept Any Player
          </button>
        </div>
      )}
      <VoteWitness voter={currentUser} hideIfVoted={true} />
    </div>
  );
};
